import {
  AppealListItem,
  AppealDetail,
  AppealStats,
  AppealsByPlayer,
  AppealsByBan,
  PaginatedAppealsResponse,
  StaffAnswersRequest,
  AppealFilters,
  JuryVote,
  JuryStageVotes,
  V2JuryStageVote,
} from '../types/appeals';

const API_BASE_URL = 'https://spy.playcdu.co/api/notes';

class AppealsApiService {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return response.json();
  }

  private buildQueryString(params: Record<string, string | number | boolean | undefined | null>): string {
    const qs = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        qs.append(key, String(value));
      }
    }
    const str = qs.toString();
    return str ? `?${str}` : '';
  }

  async listAppeals(filters?: AppealFilters): Promise<PaginatedAppealsResponse | AppealListItem[]> {
    const qs = filters ? this.buildQueryString({
      status: filters.status || undefined,
      assigned_to: filters.assigned_to || undefined,
      user_uuid: filters.user_uuid || undefined,
      ban_id: filters.ban_id || undefined,
      sort_by: filters.sort_by || undefined,
      sort_order: filters.sort_order || undefined,
      page: filters.page,
      limit: filters.limit,
    }) : '';
    return this.request(`/appeals${qs}`);
  }

  async getStats(): Promise<AppealStats> {
    return this.request<AppealStats>('/appeals/stats');
  }

  async getMyAppeals(status?: string): Promise<AppealListItem[]> {
    const qs = status ? this.buildQueryString({ status }) : '';
    return this.request<AppealListItem[]>(`/appeals/mine${qs}`);
  }

  async getAppealsByPlayer(userUuid: string): Promise<AppealsByPlayer[]> {
    return this.request<AppealsByPlayer[]>(`/appeals/by-user/${encodeURIComponent(userUuid)}`);
  }

  async getAppealsByBan(banId: number | string): Promise<AppealsByBan[]> {
    return this.request<AppealsByBan[]>(`/appeals/by-ban/${encodeURIComponent(banId)}`);
  }

  async getAppealDetail(id: number): Promise<AppealDetail> {
    return this.request<AppealDetail>(`/appeals/${encodeURIComponent(id)}`);
  }

  async getJuryVotes(id: number): Promise<JuryVote[]> {
    return this.request<JuryVote[]>(`/appeals/${encodeURIComponent(id)}/jury`);
  }

  /**
   * Jury V2 stage votes, grouped by stage. For V1 appeals the backend
   * returns `{ stage1: [], stage2: [] }`. Read-only — the panel cannot
   * manipulate the jury.
   */
  async getJuryStageVotes(id: number): Promise<JuryStageVotes> {
    return this.request<JuryStageVotes>(`/appeals/${encodeURIComponent(id)}/jury/v2`);
  }

  /** Jury V2 stage votes filtered to a single stage (flat array). */
  async getJuryStageVotesForStage(id: number, stage: 1 | 2): Promise<V2JuryStageVote[]> {
    return this.request<V2JuryStageVote[]>(`/appeals/${encodeURIComponent(id)}/jury/v2?stage=${stage}`);
  }

  async assignStaff(id: number, assignedStaffId: string): Promise<AppealDetail> {
    return this.request<AppealDetail>(`/appeals/${encodeURIComponent(id)}/assign`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assigned_staff_id: assignedStaffId }),
    });
  }

  async reassignStaff(id: number, assignedStaffId: string): Promise<AppealDetail> {
    return this.request<AppealDetail>(`/appeals/${encodeURIComponent(id)}/reassign`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assigned_staff_id: assignedStaffId }),
    });
  }

  async submitStaffAnswers(id: number, answers: StaffAnswersRequest): Promise<AppealDetail> {
    return this.request<AppealDetail>(`/appeals/${encodeURIComponent(id)}/staff-answers`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(answers),
    });
  }

  async escalateAppeal(id: number, reviewerNotes: string): Promise<AppealDetail> {
    return this.request<AppealDetail>(`/appeals/${encodeURIComponent(id)}/escalate`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reviewer_notes: reviewerNotes }),
    });
  }

  async cancelAppeal(id: number, reviewerNotes: string): Promise<AppealDetail> {
    return this.request<AppealDetail>(`/appeals/${encodeURIComponent(id)}/cancel`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reviewer_notes: reviewerNotes }),
    });
  }
}

export const appealsApiService = new AppealsApiService();
